import React, { useState, useRef } from "react";
import axios from "axios";
import { Upload, Check, AlertCircle, X } from "lucide-react";
import { useLang } from "../context/LangContext";
import { bodyTypeOptions, roleOptions } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const photoKinds = ["face", "boxer_front", "boxer_back", "slippers_front", "slippers_back"];

export default function ApplicationForm() {
  const { t } = useLang();
  const [form, setForm] = useState({
    full_name: "", artistic_name: "", age: "", nationality: "",
    street: "", street_number: "", city: "", postal_code: "", country: "",
    whatsapp: "", email: "",
    instagram: "", onlyfans: "", tiktok: "",
    height_cm: "", weight_kg: "",
    chest_cm: "", waist_cm: "", hips_cm: "", arms_cm: "", thighs_cm: "",
    underwear_size: "", body_type: "", role: "", couple_content: "No",
    sexual_orientation: "",
    experience: "", followers: "", monthly_views: "", engagement: "",
    availability: "", introduction: "",
  });

  const [photos, setPhotos] = useState({ face: null, boxer_front: null, boxer_back: null, slippers_front: null, slippers_back: null });
  const [photoPaths, setPhotoPaths] = useState({ face: "", boxer_front: "", boxer_back: "", slippers_front: "", slippers_back: "" });
  const [uploading, setUploading] = useState({ face: false, boxer_front: false, boxer_back: false, slippers_front: false, slippers_back: false });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const fileRefs = { face: useRef(), boxer_front: useRef(), boxer_back: useRef(), slippers_front: useRef(), slippers_back: useRef() };

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handlePhoto = async (kind, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError("File too large (max 10MB)");
      return;
    }
    setUploading({ ...uploading, [kind]: true });
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const res = await axios.post(`${API}/upload-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos({ ...photos, [kind]: file.name });
      setPhotoPaths({ ...photoPaths, [kind]: res.data.path });
    } catch (e) {
      setError(`Upload failed for ${kind}`);
    } finally {
      setUploading({ ...uploading, [kind]: false });
    }
  };

  const removePhoto = (kind) => {
    setPhotos({ ...photos, [kind]: null });
    setPhotoPaths({ ...photoPaths, [kind]: "" });
    if (fileRefs[kind].current) fileRefs[kind].current.value = "";
  };

  const validate = () => {
    const required = ["full_name", "age", "nationality", "street", "street_number", "city", "postal_code", "country",
      "whatsapp", "email", "height_cm", "weight_kg", "underwear_size", "body_type", "role"];
    for (const k of required) {
      if (!form[k]) return `Missing field: ${k.replace(/_/g, " ")}`;
    }
    if (Number(form.age) < 18 || Number(form.age) > 35) return "Age must be between 18 and 35";
    if (Number(form.height_cm) < 170) return "Minimum height is 170cm";
    for (const k of photoKinds) {
      if (!photoPaths[k]) return `Photo missing: ${k}`;
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        age: parseInt(form.age),
        height_cm: parseFloat(form.height_cm),
        weight_kg: parseFloat(form.weight_kg),
        chest_cm: form.chest_cm ? parseFloat(form.chest_cm) : null,
        waist_cm: form.waist_cm ? parseFloat(form.waist_cm) : null,
        hips_cm: form.hips_cm ? parseFloat(form.hips_cm) : null,
        arms_cm: form.arms_cm ? parseFloat(form.arms_cm) : null,
        thighs_cm: form.thighs_cm ? parseFloat(form.thighs_cm) : null,
        photo_face: photoPaths.face,
        photo_boxer_front: photoPaths.boxer_front,
        photo_boxer_back: photoPaths.boxer_back,
        photo_slippers_front: photoPaths.slippers_front,
        photo_slippers_back: photoPaths.slippers_back,
      };
      await axios.post(`${API}/applications`, payload);
      setSuccess(true);
      window.scrollTo({ top: document.getElementById("apply").offsetTop - 100, behavior: "smooth" });
    } catch (e) {
      setError(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section id="apply" className="py-32 bg-[#0a0a0a]">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center">
          <div className="w-20 h-20 border border-[#00E5FF] mx-auto flex items-center justify-center mb-8">
            <Check size={32} className="text-[#00E5FF]" />
          </div>
          <h2 data-testid="success-title" className="font-display uppercase text-4xl sm:text-5xl text-white mb-6 tracking-tight">{t.form.successTitle}</h2>
          <p className="text-neutral-400 leading-relaxed text-lg">{t.form.successMsg}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="apply" data-testid="application-form-section" className="py-24 lg:py-32 bg-[#0a0a0a] relative">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <span className="block w-12 h-px bg-[#00E5FF]" />
            <span className="label-eyebrow text-[#00E5FF]">{t.form.eyebrow}</span>
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
            {t.form.title}
          </h2>
        </div>

        <form onSubmit={submit} className="space-y-16">
          <FormSection title={t.form.sectionPersonal}>
            <Field label={t.form.fullName} name="full_name" value={form.full_name} onChange={handle} required />
            <Field label={t.form.artisticName} name="artistic_name" value={form.artistic_name} onChange={handle} />
            <Field label={t.form.age} name="age" type="number" min="18" max="35" value={form.age} onChange={handle} required />
            <Field label={t.form.nationality} name="nationality" value={form.nationality} onChange={handle} required />
          </FormSection>

          <FormSection title={t.form.sectionContact}>
            <Field label={t.form.street} name="street" value={form.street} onChange={handle} required full />
            <Field label={t.form.streetNumber} name="street_number" value={form.street_number} onChange={handle} required />
            <Field label={t.form.city} name="city" value={form.city} onChange={handle} required />
            <Field label={t.form.postalCode} name="postal_code" value={form.postal_code} onChange={handle} required />
            <Field label={t.form.country} name="country" value={form.country} onChange={handle} required />
            <Field label={t.form.whatsapp} name="whatsapp" value={form.whatsapp} onChange={handle} required />
            <Field label={t.form.email} name="email" type="email" value={form.email} onChange={handle} required />
          </FormSection>

          <FormSection title={t.form.sectionSocial}>
            <Field label={t.form.instagram} name="instagram" placeholder="@" value={form.instagram} onChange={handle} />
            <Field label={t.form.onlyfans} name="onlyfans" placeholder="@" value={form.onlyfans} onChange={handle} />
            <Field label={t.form.tiktok} name="tiktok" placeholder="@" value={form.tiktok} onChange={handle} />
          </FormSection>

          <FormSection title={t.form.sectionMeasures}>
            <Field label={t.form.height} name="height_cm" type="number" min="100" max="250" value={form.height_cm} onChange={handle} required />
            <Field label={t.form.weight} name="weight_kg" type="number" min="40" max="200" value={form.weight_kg} onChange={handle} required />
            <Field label={t.form.chest} name="chest_cm" type="number" value={form.chest_cm} onChange={handle} />
            <Field label={t.form.waist} name="waist_cm" type="number" value={form.waist_cm} onChange={handle} />
            <Field label={t.form.hips} name="hips_cm" type="number" value={form.hips_cm} onChange={handle} />
            <Field label={t.form.arms} name="arms_cm" type="number" value={form.arms_cm} onChange={handle} />
            <Field label={t.form.thighs} name="thighs_cm" type="number" value={form.thighs_cm} onChange={handle} />
            <Field label={t.form.underwearSize} name="underwear_size" placeholder="S / M / L / XL" value={form.underwear_size} onChange={handle} required />
          </FormSection>

          <FormSection title={t.form.sectionProfile}>
            <SelectField label={t.form.bodyType} name="body_type" value={form.body_type} onChange={handle} options={bodyTypeOptions} required />
            <SelectField label={t.form.role} name="role" value={form.role} onChange={handle} options={roleOptions} required />
            <div className="md:col-span-2">
              <label className="intimo-label">{t.form.coupleContent}</label>
              <div className="flex gap-3 mt-2">
                {["Yes", "No"].map((v) => (
                  <button
                    type="button"
                    key={v}
                    data-testid={`couple-${v.toLowerCase()}`}
                    onClick={() => setForm({ ...form, couple_content: v })}
                    className={`px-6 py-3 border text-xs tracking-[0.2em] uppercase transition-all ${
                      form.couple_content === v
                        ? "bg-[#00E5FF] text-black border-[#00E5FF]"
                        : "bg-transparent text-neutral-400 border-white/10 hover:border-[#00E5FF]"
                    }`}
                  >
                    {v === "Yes" ? t.form.yes : t.form.no}
                  </button>
                ))}
              </div>
              <p className="text-xs text-neutral-500 mt-3 italic">{t.form.coupleNote}</p>
            </div>
            <div className="md:col-span-2">
              <label className="intimo-label">{t.form.sexualOrientation}</label>
              <textarea
                data-testid="field-sexual_orientation"
                name="sexual_orientation"
                value={form.sexual_orientation}
                onChange={handle}
                rows={2}
                className="intimo-input resize-none"
                placeholder=""
              />
            </div>
          </FormSection>

          <FormSection title={t.form.sectionContent}>
            <Field label={t.form.followers} name="followers" value={form.followers} onChange={handle} />
            <Field label={t.form.monthlyViews} name="monthly_views" value={form.monthly_views} onChange={handle} />
            <Field label={t.form.engagement} name="engagement" placeholder="e.g. 4.5%" value={form.engagement} onChange={handle} />
            <Field label={t.form.availability} name="availability" placeholder="Weekdays, weekends, travel" value={form.availability} onChange={handle} />
            <div className="md:col-span-2">
              <label className="intimo-label">{t.form.experience}</label>
              <textarea
                data-testid="field-experience"
                name="experience"
                value={form.experience}
                onChange={handle}
                rows={3}
                className="intimo-input resize-none"
              />
            </div>
          </FormSection>

          <FormSection title={t.form.sectionPhotos} fullGrid>
            <div className="md:col-span-3 mb-6 flex items-start gap-3 border border-[#00E5FF]/30 bg-[#00E5FF]/5 px-4 py-3">
              <span className="text-[#00E5FF] text-base leading-none mt-0.5">●</span>
              <p className="text-sm text-neutral-300 leading-relaxed">{t.form.photoBgNote}</p>
            </div>
            <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {photoKinds.map((kind) => (
                <PhotoUpload
                  key={kind}
                  kind={kind}
                  label={t.form.photos[kind]}
                  hint={t.form.photoHint}
                  file={photos[kind]}
                  uploading={uploading[kind]}
                  onChange={(f) => handlePhoto(kind, f)}
                  onRemove={() => removePhoto(kind)}
                  fileRef={fileRefs[kind]}
                />
              ))}
            </div>
          </FormSection>

          <FormSection title={t.form.sectionMessage} fullGrid>
            <div className="md:col-span-3">
              <label className="intimo-label">{t.form.introduction}</label>
              <textarea
                data-testid="field-introduction"
                name="introduction"
                value={form.introduction}
                onChange={handle}
                rows={5}
                className="intimo-input resize-none"
                placeholder="..."
              />
            </div>
          </FormSection>

          {error && (
            <div data-testid="form-error" className="flex items-start gap-3 p-4 border border-[#FF3366]/50 bg-[#FF3366]/5 text-[#FF3366]">
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="border-t border-white/5 pt-10">
            <p className="text-xs text-neutral-500 leading-relaxed mb-6 max-w-2xl">{t.form.disclaimer}</p>
            <button
              type="submit"
              data-testid="submit-application-btn"
              disabled={submitting}
              className="btn-solid text-base px-12 py-5"
            >
              {submitting ? t.form.submitting : `${t.form.submit} →`}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

const FormSection = ({ title, children, fullGrid }) => (
  <div className="border-t border-white/5 pt-12">
    <h3 className="font-display uppercase tracking-[0.2em] text-sm text-[#00E5FF] mb-8">{title}</h3>
    <div className={fullGrid ? "" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6"}>{children}</div>
  </div>
);

const Field = ({ label, name, type = "text", value, onChange, required, placeholder, full, min, max }) => (
  <div className={full ? "md:col-span-2 lg:col-span-3" : ""}>
    <label className="intimo-label">{label}{required && <span className="text-[#00E5FF] ml-1">*</span>}</label>
    <input
      data-testid={`field-${name}`}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      min={min}
      max={max}
      className="intimo-input"
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options, required }) => (
  <div>
    <label className="intimo-label">{label}{required && <span className="text-[#00E5FF] ml-1">*</span>}</label>
    <select
      data-testid={`field-${name}`}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="intimo-input cursor-pointer"
    >
      <option value="" disabled>—</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const PhotoUpload = ({ kind, label, hint, file, uploading, onChange, onRemove, fileRef }) => (
  <div>
    <label className="intimo-label">{label}<span className="text-[#00E5FF] ml-1">*</span></label>
    <div
      data-testid={`photo-drop-${kind}`}
      className={`file-drop ${file ? "has-file" : ""}`}
      onClick={() => !file && fileRef.current?.click()}
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid={`photo-input-${kind}`}
        onChange={(e) => onChange(e.target.files?.[0])}
      />
      {uploading ? (
        <div className="py-6">
          <div className="animate-spin w-6 h-6 border-2 border-[#00E5FF] border-t-transparent mx-auto rounded-full" />
          <p className="text-xs text-neutral-400 mt-3">Uploading...</p>
        </div>
      ) : file ? (
        <div className="py-2">
          <Check size={20} className="text-[#00E5FF] mx-auto mb-2" />
          <p className="text-xs text-white truncate px-2">{file}</p>
          <button
            type="button"
            data-testid={`photo-remove-${kind}`}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="mt-3 text-xs text-neutral-500 hover:text-[#FF3366] inline-flex items-center gap-1"
          >
            <X size={12} /> Remove
          </button>
        </div>
      ) : (
        <div className="py-4">
          <Upload size={20} className="text-neutral-500 mx-auto mb-2" />
          <p className="text-xs text-neutral-300 font-medium">Click to upload</p>
          <p className="text-[10px] text-neutral-600 mt-1">{hint}</p>
        </div>
      )}
    </div>
  </div>
);
